// @ts-nocheck
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  TextField,
  Typography,
  DialogActions,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';

import DialogTransition from '../../../Common/Transition/DialogTransition';
import {
  copyToClipboard,
  groupTemplateList,
  getTemplateAttributes,
  isValueEmpty,
} from '../../../../utils/Utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import renderTemplateVersionOption from '../../../Common/TemplateVersionOption';
import SectionAttributePopover from '../../../Common/SectionAttributePopover';
import useTemplateVersion from '../../../../hooks/Configuration/Templates/useTemplateVersion';

const convertToFormatString = (data) => {
  const { value } = data;

  if (value) {
    if (value?.data_type === 'System') {
      return `{{system.${value?.name ? value?.name : ''}}}`;
    }
    return `{{attributes.${value?.name ? value?.name : ''}}}`;
  }

  return '';
};

const handleIsOptionEqualToValue = (option, val) =>
  option?.value === val?.value;
const handleGetOptionLabel = (option) => option?.label;
const groupBy = (option) => option?.type || '';

const SmartValuesModal = ({
  open,
  handleClose,
  handleOnChange,
  value,
  attributes = EMPTY_ARRAY,
  AttributesOption = EMPTY_OBJECT,
  isConditionalModal = false,
  loadingTemplates = false,
  getCustomEntityTemplate,
  entityList,
  insertAtCursor = false,
  autoSelect = false,
  showSimpleLabel = false,
  disableTemplateSelection = false,
}) => {
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [templateVersionList, setTemplateVersionList] = useState([]);
  const [templateAttributes, setTemplateAttributes] = useState([]);

  const { getTemplateVersionList } = useTemplateVersion();

  // TODO
  const templateOptions = useMemo(() => {
    if (isConditionalModal) {
      return EMPTY_ARRAY;
    }

    return groupTemplateList(entityList);
  }, [entityList, isConditionalModal]);

  const smartValuesFormikForm = useFormik({
    initialValues: {
      value: '',
      template: null,
      templateVersion: null,
    },
    validationSchema: null,
    onSubmit: (values) => {
      // Append the selected value into the derived formula
      if (insertAtCursor) {
        handleOnChange(convertToFormatString(values));
      } else {
        handleOnChange(`${value}${convertToFormatString(values)}`);
      }
      // eslint-disable-next-line no-use-before-define
      handleCloseModal();
    },
  });

  const {
    value: selectedAttributeValue,
    template: selectedTemplateValue,
    templateVersion: selectedTemplateVersion,
  } = smartValuesFormikForm.values;

  useEffect(() => {
    if (
      open &&
      autoSelect &&
      !isConditionalModal &&
      templateOptions?.length === 1
    ) {
      smartValuesFormikForm.setFieldValue('template', templateOptions[0]);
    }
  }, [open, isConditionalModal, autoSelect, templateOptions]);

  /**
   * Closes the smart values modal and resets the formik form.
   * @name handleCloseModal
   * @function
   * @returns {void}
   * @example handleCloseModal()
   */
  const handleCloseModal = useCallback(() => {
    handleClose();
    smartValuesFormikForm.resetForm();
    setTemplateVersionList([]);
    setTemplateAttributes([]);
    setLoadingAttributes(false);
  }, [handleClose, smartValuesFormikForm]);

  /**
   * Handles template selection change
   */
  const handleChangeTemplate = useCallback(
    (_, newValue) => {
      smartValuesFormikForm?.setValues((prev) => ({
        ...prev,
        template: newValue,
        templateVersion: null,
        value: '',
      }));
      setTemplateVersionList([]);
      setTemplateAttributes([]);
    },
    [smartValuesFormikForm, setTemplateVersionList, setTemplateAttributes]
  );

  const handleChangeTemplateVersion = useCallback(
    (_, newValue) => {
      smartValuesFormikForm?.setValues((prev) => ({
        ...prev,
        templateVersion: newValue || null,
        value: '',
      }));
      setTemplateAttributes([]);
    },
    [smartValuesFormikForm, setTemplateAttributes]
  );

  /**
   * Copies the selected smart value to the clipboard.
   * @name handleCopyText
   * @function
   * @returns {void}
   * @example handleCopyText()
   */
  const handleCopyText = useCallback(
    () => copyToClipboard(convertToFormatString(smartValuesFormikForm?.values)),
    [convertToFormatString, smartValuesFormikForm?.values, copyToClipboard]
  );

  /**
   * Handles the change of the selected field and sets the selected field value in the formik form.
   * @name handleChangeField
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   * @param {string} newValue - The new selected value.
   * @returns {void}
   * @example handleChangeField(event, newValue)
   */
  const handleChangeField = useCallback(
    (_, newValue) => smartValuesFormikForm?.setFieldValue('value', newValue),
    [smartValuesFormikForm]
  );

  /**
   * Renders the template input field.
   * @name handleRenderInputTemplate
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   * @returns {React.ReactNode} The rendered input field.
   * @example handleRenderInputTemplate(event)
   */
  const handleRenderInputTemplate = useCallback(
    (params) => (
      <TextField
        {...params}
        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        label="Template"
        placeholder="All templates"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loadingTemplates ? (
                <CircularProgress size={20} sx={{ mr: 2 }} />
              ) : null}
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [loadingTemplates]
  );

  const handleRenderInputTemplateVersionType = useCallback(
    (params) => (
      <TextField
        {...params}
        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        label="Template Version"
        placeholder="Select template version"
        spellCheck={true}
      />
    ),
    []
  );

  /**
   * Renders the input field.
   * @name handleRenderInputField
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} event - The input change event.
   * @returns {React.ReactNode} The rendered input field.
   * @example handleRenderInputField(event)
   */
  const handleRenderInputField = useCallback(
    (params) => (
      <TextField
        {...params}
        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        label="Field"
        placeholder="Select field"
        spellCheck={true}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loadingAttributes ? (
                <CircularProgress size={20} sx={{ mr: 2 }} />
              ) : null}
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [loadingAttributes]
  );

  const handleAddTextTitle = useMemo(() => {
    if (!smartValuesFormikForm?.values?.value)
      return 'Please select a value to add';
    return 'Add';
  }, [smartValuesFormikForm?.values?.value]);

  const handleCopyTextTitle = useMemo(() => {
    if (!smartValuesFormikForm?.values?.value)
      return 'Please select a value to copy';
    return 'Copy';
  }, [smartValuesFormikForm?.values?.value]);

  const attributesOptions = useMemo(() => {
    if (isConditionalModal) {
      return AttributesOption;
    }

    if (templateAttributes?.length > 0) {
      return templateAttributes;
    }

    if (!selectedTemplateValue) {
      return attributes;
    }
    return [];
  }, [
    isConditionalModal,
    templateAttributes,
    attributes,
    AttributesOption,
    selectedTemplateValue,
  ]);

  const handleGetTemplateVersionList = useCallback(
    (template) => {
      getTemplateVersionList({ template_id: template?.id }).then((res) => {
        if (res?.data) {
          const updatedList = (res?.data?.versions ?? []).map((item) => ({
            ...item,
            value: item.id,
            label: item.version_number,
          }));

          const defaultVersion =
            updatedList?.find((item) => item?.is_default) ||
            updatedList?.[0] ||
            null;

          setTemplateVersionList(updatedList);
          smartValuesFormikForm?.setFieldValue(
            'templateVersion',
            defaultVersion
          );
        }
      });
    },
    [getTemplateVersionList, smartValuesFormikForm]
  );

  const handleRenderFieldOptions = useCallback(
    (props, option) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={4}
        {...props}
        key={option?.value}
        title={`${option?.label}`}
      >
        <Typography mb={0.2} noWrap width={300}>
          {showSimpleLabel ? option?.label : `${option?.id} : ${option?.label}`}
        </Typography>
      </Box>
    ),
    [showSimpleLabel]
  );

  useEffect(() => {
    if (!isValueEmpty(selectedTemplateValue)) {
      handleGetTemplateVersionList(selectedTemplateValue);
      return;
    }
  }, [selectedTemplateValue]);

  useEffect(() => {
    if (!selectedTemplateValue || !selectedTemplateVersion) {
      return;
    }

    setLoadingAttributes(true);
    const fetchParams = {
      template_id: selectedTemplateValue?.id,
      version_id: selectedTemplateVersion?.id,
    };

    getCustomEntityTemplate(fetchParams, false)
      .then((res) => {
        if (res?.data) {
          setTemplateAttributes(getTemplateAttributes(res?.data) || []);
        }
      })
      .finally(() => {
        setLoadingAttributes(false);
      });
  }, [selectedTemplateValue, selectedTemplateVersion]);

  return (
    <Dialog
      disableBackdropClick
      onClose={handleCloseModal}
      open={open}
      maxWidth="sm"
      fullWidth={true}
      aria-labelledby="edit-if-node-modal"
      TransitionComponent={DialogTransition}
    >
      <DialogTitle>
        <Box component="span" aria-live="polite" aria-atomic="true">
          Smart Values
        </Box>
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleCloseModal}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid container>
          <Grid container item xs={12} spacing={2}>
            {!isConditionalModal && (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    {...smartValuesFormikForm.getFieldProps('template')}
                    fullWidth
                    options={templateOptions}
                    value={selectedTemplateValue || null}
                    onChange={handleChangeTemplate}
                    popupIcon={<DropdownArrow />}
                    getOptionLabel={handleGetOptionLabel}
                    isOptionEqualToValue={handleIsOptionEqualToValue}
                    groupBy={groupBy}
                    disableClearable={['', undefined].includes(
                      selectedTemplateValue
                    )}
                    renderInput={handleRenderInputTemplate}
                    loading={loadingTemplates}
                    disabled={loadingTemplates || disableTemplateSelection}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    {...smartValuesFormikForm.getFieldProps('templateVersion')}
                    fullWidth
                    options={templateVersionList || EMPTY_ARRAY}
                    value={selectedTemplateVersion || null}
                    onChange={handleChangeTemplateVersion}
                    renderOption={renderTemplateVersionOption}
                    getOptionLabel={handleGetOptionLabel}
                    isOptionEqualToValue={handleIsOptionEqualToValue}
                    popupIcon={<DropdownArrow />}
                    renderInput={handleRenderInputTemplateVersionType}
                    disabled={
                      loadingTemplates ||
                      disableTemplateSelection ||
                      !selectedTemplateValue
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <SectionAttributePopover
                options={attributesOptions}
                value={selectedAttributeValue || null}
                onChange={handleChangeField}
                getOptionLabel={handleGetOptionLabel}
                renderInput={handleRenderInputField}
                renderOption={handleRenderFieldOptions}
                loading={loadingAttributes}
                enableSectionFilter={
                  selectedTemplateValue?.value !== '' &&
                  (isConditionalModal || templateAttributes?.length > 0)
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h4">
                {convertToFormatString(smartValuesFormikForm?.values)}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal} variant="outlined" title="Cancel">
          Cancel
        </Button>
        <Button
          onClick={smartValuesFormikForm?.handleSubmit}
          disabled={!selectedAttributeValue}
          variant="contained"
          title={handleAddTextTitle}
        >
          Add
        </Button>
        <Button
          onClick={handleCopyText}
          title={handleCopyTextTitle}
          disabled={!selectedAttributeValue}
          variant="contained"
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SmartValuesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleOnChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  AttributesOption: PropTypes.oneOfType([PropTypes.object]).isRequired,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  isConditionalModal: PropTypes.bool,
  loadingTemplates: PropTypes.bool,
  getCustomEntityTemplate: PropTypes.func.isRequired,
  entityList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  insertAtCursor: PropTypes.bool,
  autoSelect: PropTypes.bool,
  showSimpleLabel: PropTypes.bool,
  disableTemplateSelection: PropTypes.bool,
};

export default SmartValuesModal;
